import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { CatalogAttributeDefinitionService } from '@features/catalog/services/catalog-attribute-definition.service';
import { CatalogAttributeValueService } from '@features/catalog/services/catalog-attribute-value.service';
import {
  AttributeDefinitionMutationResult,
  ProductAttributeDefinition,
} from '@features/catalog/models/product-attribute-definition.models';
import { AttributeDefinitionOptionDraft } from '@features/catalog/models/attribute-definition-option.models';

export interface AttributeDefinitionFormSaveRequest {
  readonly definitionId: string | null;
  readonly name: string;
  readonly slug: string | null;
  readonly position: number;
  readonly active: boolean;
  /** Valores activos al abrir el form (solo edit). */
  readonly baselineOptions: readonly AttributeDefinitionOptionDraft[];
  /** Valores actuales en el form. */
  readonly draftOptions: readonly AttributeDefinitionOptionDraft[];
}

@Injectable()
export class AttributeDefinitionFormOrchestrationService {
  private readonly definitions = inject(CatalogAttributeDefinitionService);
  private readonly values = inject(CatalogAttributeValueService);

  save(request: AttributeDefinitionFormSaveRequest): Observable<AttributeDefinitionMutationResult> {
    const definition$ = request.definitionId
      ? this.definitions.update({
          id: request.definitionId,
          name: request.name,
          slug: request.slug,
          position: request.position,
          active: request.active,
        })
      : this.definitions.create({
          name: request.name,
          slug: request.slug,
          position: request.position,
          active: request.active,
        });

    return definition$.pipe(
      concatMap((result) => {
        if (result.errors.length || !result.definition) {
          return of(result);
        }
        return this.syncOptions(result.definition, request).pipe(
          map((errors) => ({
            definition: result.definition,
            errors,
          })),
        );
      }),
    );
  }

  private syncOptions(
    definition: ProductAttributeDefinition,
    request: AttributeDefinitionFormSaveRequest,
  ): Observable<string[]> {
    const definitionId = definition.id;
    const baselineById = new Map(
      request.baselineOptions
        .filter((opt) => !!opt.id)
        .map((opt) => [opt.id as string, opt] as const),
    );
    const draftIds = new Set(
      request.draftOptions.map((opt) => opt.id).filter((id): id is string => !!id),
    );

    const creates = request.draftOptions.filter((opt) => !opt.id);
    const updates = request.draftOptions.filter((opt) => {
      if (!opt.id) {
        return false;
      }
      const baseline = baselineById.get(opt.id);
      return baseline != null && baseline.value.trim() !== opt.value.trim();
    });
    const removes = [...baselineById.keys()].filter((id) => !draftIds.has(id));

    type Step = () => Observable<string[]>;
    const steps: Step[] = [];

    creates.forEach((opt, index) => {
      const position = request.draftOptions.indexOf(opt);
      steps.push(() =>
        this.values
          .create({
            productAttributeDefinitionId: definitionId,
            value: opt.value.trim(),
            position: position >= 0 ? position : index,
            active: true,
          })
          .pipe(map((r) => r.errors)),
      );
    });

    updates.forEach((opt) => {
      steps.push(() =>
        this.values
          .update({
            id: opt.id as string,
            value: opt.value.trim(),
          })
          .pipe(map((r) => r.errors)),
      );
    });

    removes.forEach((id) => {
      steps.push(() => this.values.destroy(id).pipe(map((r) => r.errors)));
    });

    if (steps.length === 0) {
      return of([]);
    }

    return steps.reduce<Observable<string[]>>(
      (acc, step) =>
        acc.pipe(
          concatMap((prevErrors) => {
            if (prevErrors.length) {
              return of(prevErrors);
            }
            return step().pipe(map((next) => [...prevErrors, ...next]));
          }),
        ),
      of([]),
    );
  }
}
